import {
  SimpleProjectionAssumptions,
  SimpleProjectionYearResult,
  GrowthOverrides,
} from '../../../../types/SimpleProjection';

interface CalculateProjectionsParams {
  assumptions: SimpleProjectionAssumptions;
  revenueGrowthPct: number; // Default growth rate as decimal, e.g., 0.10 for 10%
  netIncomeMarginPct: number; // Default margin as decimal, e.g., 0.15 for 15%
  peRatio: number; // User-specified PE ratio (low = peRatio, high = peRatio × 1.5)
  dilutionRate: number; // Annual share dilution as decimal (positive = issuance, negative = buybacks). Compounds yearly.
  numberOfYears?: number; // Default 5
  growthOverrides?: GrowthOverrides; // Per-year manual overrides for growth rates
}

/**
 * Calculates simple growth projections based on revenue growth, margin assumptions, and PE multiple.
 *
 * Key features:
 * - PE ratios use user-specified value (peRatioLow = peRatio, peRatioHigh = peRatio × 1.5)
 * - Share prices are calculated as ranges: EPS × peRatioLow and EPS × peRatioHigh
 * - Supports per-year overrides for revenue growth % and margin %
 * - When a year's growth is overridden, it affects that year's revenue calculation
 *
 * Returns an array of year-by-year projections including historical year 0.
 */
export function calculateSimpleProjections(params: CalculateProjectionsParams): SimpleProjectionYearResult[] {
  const { assumptions, revenueGrowthPct, netIncomeMarginPct, peRatio, dilutionRate, numberOfYears = 5, growthOverrides = {} } = params;

  const results: SimpleProjectionYearResult[] = [];
  const baseFiscalYear = parseInt(assumptions.fiscalYear, 10);

  // Default PE ratios from sidebar (can be overridden per-year)
  const defaultPeRatioLow = peRatio;
  const defaultPeRatioHigh = peRatio * 1.5;

  // Authoritative diluted share count from the source filing. Always
  // positive even for loss-makers; do NOT derive from netIncome/eps —
  // the upstream data occasionally returns rows where those two disagree
  // on sign, producing a negative or zero derived count. Projections
  // compound this by dilutionRate yearly so EPS reflects the user's
  // assumption about future share issuance (SBC, secondaries) or
  // buybacks.
  const baseShares = assumptions.weightedAverageShsOutDil;

  // Applying a P/E multiple to a negative EPS yields a negative share
  // price, which is not editorially meaningful. Return null so consumers
  // can render "—" or skip the data point.
  const projectSharePrice = (eps: number, multiple: number): number | null =>
    eps < 0 ? null : eps * multiple;

  // Year 0: Historical data (most recent fiscal year) - always locked, not editable.
  // year0NetIncome uses the same attributable basis (revenue × margin) as the
  // projection years so year 0 ties out internally with the displayed EPS and
  // margin. Headline net income is still available via assumptions.netIncome
  // for the tooltip explaining why the displayed figure can differ from what
  // a reader sees on other sites.
  const year0Revenue = assumptions.revenue;
  const year0Margin = assumptions.netIncomeMargin;
  const year0NetIncome = year0Revenue * year0Margin;
  const year0Eps = assumptions.epsDiluted;

  // Share prices based on PE range
  const year0SharePriceLow = projectSharePrice(year0Eps, defaultPeRatioLow);
  const year0SharePriceHigh = projectSharePrice(year0Eps, defaultPeRatioHigh);

  results.push({
    year: 0,
    yearLabel: assumptions.fiscalYear,
    isHistorical: true,
    revenue: year0Revenue,
    revenueGrowthPct: 0, // No growth for historical year (it's the base)
    netIncome: year0NetIncome,
    netIncomeGrowthPct: 0, // No growth for historical year
    netIncomeMargin: year0Margin,
    eps: year0Eps,
    peRatioLow: defaultPeRatioLow,
    peRatioHigh: defaultPeRatioHigh,
    sharePriceLow: year0SharePriceLow,
    sharePriceHigh: year0SharePriceHigh,
    revenueGrowthOverridden: false,
    marginOverridden: false,
    peLowOverridden: false,
    peHighOverridden: false,
  });

  // Years 1-5: Projected data
  let currentRevenue = year0Revenue;
  let previousNetIncome = year0NetIncome;

  for (let year = 1; year <= numberOfYears; year++) {
    const yearLabel = (baseFiscalYear + year).toString();
    const yearOverride = growthOverrides[yearLabel];

    // Check for growth rate override
    const revenueGrowthOverridden = yearOverride?.revenueGrowthPct !== undefined;
    const effectiveGrowthRate = revenueGrowthOverridden ? yearOverride.revenueGrowthPct! : revenueGrowthPct;

    // Calculate revenue with growth rate
    currentRevenue = currentRevenue * (1 + effectiveGrowthRate);

    // Check for margin override, otherwise use global margin
    const marginOverridden = yearOverride?.netIncomeMarginPct !== undefined;
    const effectiveMargin = marginOverridden ? yearOverride.netIncomeMarginPct! : netIncomeMarginPct;

    // Calculate net income based on revenue and margin (Wall Street methodology)
    const currentNetIncome = currentRevenue * effectiveMargin;

    // Calculate YoY net income growth. Growth % from a non-positive base is
    // not meaningful (turnaround years cross zero), so return NaN and let
    // the display render an em-dash.
    const netIncomeGrowthPct = previousNetIncome > 0
      ? (currentNetIncome - previousNetIncome) / previousNetIncome
      : NaN;

    // Project share count by compounding dilution from year 0. Positive
    // dilutionRate inflates the share count year-over-year (issuance);
    // negative dilutionRate shrinks it (buybacks). EPS = NI / projected
    // shares so the user's dilution assumption directly affects per-share
    // valuation.
    const projectedShares = baseShares * Math.pow(1 + dilutionRate, year);
    const currentEps = projectedShares > 0 ? currentNetIncome / projectedShares : 0;

    // Check for per-year PE ratio overrides
    const peLowOverridden = yearOverride?.peRatioLow !== undefined;
    const peHighOverridden = yearOverride?.peRatioHigh !== undefined;
    const effectivePeLow = peLowOverridden ? yearOverride.peRatioLow! : defaultPeRatioLow;
    const effectivePeHigh = peHighOverridden ? yearOverride.peRatioHigh! : defaultPeRatioHigh;

    // Share prices null when EPS is negative — see projectSharePrice above.
    const sharePriceLow = projectSharePrice(currentEps, effectivePeLow);
    const sharePriceHigh = projectSharePrice(currentEps, effectivePeHigh);

    results.push({
      year,
      yearLabel,
      isHistorical: false,
      revenue: currentRevenue,
      revenueGrowthPct: effectiveGrowthRate,
      netIncome: currentNetIncome,
      netIncomeGrowthPct,
      netIncomeMargin: effectiveMargin,
      eps: currentEps,
      peRatioLow: effectivePeLow,
      peRatioHigh: effectivePeHigh,
      sharePriceLow,
      sharePriceHigh,
      revenueGrowthOverridden,
      marginOverridden,
      peLowOverridden,
      peHighOverridden,
    });

    // Update previous net income for next iteration's growth calculation
    previousNetIncome = currentNetIncome;
  }

  return results;
}
